import { Injectable } from '@angular/core';

import { switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { MessageType, ProblemDetails } from '../../../../core/models';
import * as fromRoot from '../../../../core/store';

import * as fromActions from '../actions';
import * as fromModels from '../../models';
import * as fromServices from '../../services';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class OpusJobsEffects {
  constructor(private actions$: Actions, private opusJobsService: fromServices.OpusJobsService) {}

  loadOpusJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.OpusJobsActionTypes.LoadOpusJobs),
      switchMap((action: fromActions.LoadOpusJobs) => {
        return this.opusJobsService.getOpusJobs(action.payload).pipe(
          map((results: fromModels.OpusJob[]) => {
            return new fromActions.LoadOpusJobsSuccess(results);
          }),
          catchError((res: HttpErrorResponse) => of(new fromActions.LoadOpusJobsFailure(res)))
        );
      })
    )
  );

  loadOpusJobsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.OpusJobsActionTypes.LoadOpusJobsFailure),
      switchMap((action: fromActions.LoadOpusJobsFailure) =>
        of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        )
      )
    )
  );

  updateOpusJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.OpusJobsActionTypes.UpdateOpusJobs),
      switchMap((action: fromActions.UpdateOpusJobs) => {
        return this.opusJobsService.updateOpusJobs(action.payload.changed).pipe(
          map((results: fromModels.OpusJob[]) => {
            return new fromActions.UpdateOpusJobsSuccess(action.payload);
          }),
          catchError((res: HttpErrorResponse) => of(new fromActions.UpdateOpusJobsFailure(res)))
        );
      })
    )
  );

  updateOpusJobsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.OpusJobsActionTypes.UpdateOpusJobsSuccess),
      switchMap((action: fromActions.UpdateOpusJobsSuccess) =>
        of(
          new fromRoot.DisplayMessage({
            message: `Opus Jobs updated successfully! Changes will be applied in ${action.payload.changed[0].updateApplyIntervalInSeconds} seconds.`,
            messageType: MessageType.success,
            toast: false
          })
        )
      )
    )
  );

  updateOpusJobsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.OpusJobsActionTypes.UpdateOpusJobsFailure),
      switchMap((action: fromActions.UpdateOpusJobsFailure) =>
        of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        )
      )
    )
  );

  private getErrorMessage(response: HttpErrorResponse | string): string {
    if (typeof response === 'string') return response;

    if (response.status === 0) {
      return 'Network error. Please check your internet connection.';
    }

    if (response.status === 400 && response.error) {
      try {
        if (typeof response.error === 'string') return response.error;

        const problemDetails = response.error as ProblemDetails;
        if (problemDetails) {
          const firstError = problemDetails.errors ? problemDetails.errors[Object.keys(problemDetails.errors)[0]] : null;
          if (firstError) return firstError;
          else if (problemDetails.title) return problemDetails.title;
        }
      } catch (ex) {
        console.warn(ex);
      }
    }

    return response.error ? response.error.message : response.message;
  }
}
